import React, {useEffect, useRef, useState} from 'react';
import {useQuery} from "@tanstack/react-query";
import {useParams} from "react-router";
import {GoogleReCaptchaProvider, useGoogleReCaptcha} from "react-google-recaptcha-v3";
import {FaCircleCheck} from "react-icons/fa6";
import {Modal} from "../../../components/modal/modal.jsx";
import {navigateHook} from "../../../hooks/use-navigate.js";
import projectUtils from "../../../utils/project-utils.js";
import loginService from "../../../services/web/login-service.js";
import AuthLayout from "../../../components/auth-layout/auth-layout.jsx";
import LoginCard from "./components/login-card.jsx";
import PasswordRecoveryModal from "./components/password-recovery-modal.jsx";
import TwoFactorModal from "./components/two-factor-modal.jsx";
import TechnicalSupportModal from "./components/technical-support-modal.jsx";
import {buildLoginData, RECAPTCHA_SITE_KEY, translateServerErrorMessage} from "./utils/login-utils.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";

const ENTER_LOGIN_COOLDOWN_MS = 3000;

const LoginContent = ({loginInfo}) => {
    const {login} = useParams();
    const navigate = navigateHook();
    const {executeRecaptcha} = useGoogleReCaptcha();

    const [form, setForm] = useState({username: "", password: ""});
    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [loginBlocked, setLoginBlocked] = useState(false);

    const [supportOpen, setSupportOpen] = useState(false);
    const [recoveryOpen, setRecoveryOpen] = useState(false);
    const [twoFactorOpen, setTwoFactorOpen] = useState(false);
    const [trustedDeviceOpen, setTrustedDeviceOpen] = useState(false);
    const [twoFactor, setTwoFactor] = useState(null);
    const lastEnterLoginAt = useRef(0);

    useEffect(() => {
        if (!login) return;

        setForm({username: login, password: ""});
        setRecoveryOpen(true);
    }, [login]);

    useEffect(() => {
        const errorParam = new URLSearchParams(window.location.search).get("error_message");
        if (errorParam) setErrorMessage(errorParam);
    }, []);

    const handleInput = ({target: {id, value}}) => {
        setForm(prev => ({...prev, [id]: value}));
        setLoginBlocked(false);
        lastEnterLoginAt.current = 0;
    };

    const goHome = () => navigate("/home");

    const onLoginSuccess = (data) => {
        if (data?.trustedDevice) {
            setTrustedDeviceOpen(true);
            setTimeout(goHome, 1500);
            return;
        }

        goHome();
    };

    const handleLoginError = (error) => {
        setLoading(false);

        if (!error.response) {
            setErrorMessage("Erro ao conectar no servidor");
            return;
        }

        const {status, data} = error.response;

        if (status === 401) {
            setLoginBlocked(true);
        }

        if (status >= 500) {
            setErrorMessage("Servidor indisponível. Aguarde alguns minutos e tente novamente. Se o problema persistir entre em contato com o suporte técnico.");
            return;
        }

        const message = data?.message ?? data;

        switch (message) {
            case "two-factor.authentication.required":
                if (data?.twoFactor) {
                    setTwoFactor(data.twoFactor);
                    setTwoFactorOpen(true);
                } else {
                    setErrorMessage("Erro ao conectar no servidor");
                }
                break;
            case "invalid.credentials":
                setErrorMessage("Usuário ou senha inválidos.");
                break;
            case "user.limit.reached":
                setErrorMessage("Limite de acessos simultâneos atingido.");
                break;
            case "login.is.blocked":
                projectUtils.swalMessage(
                    "warning",
                    "Acesso bloqueado",
                    "Seu acesso ao sistema está bloqueado. <br/> Entre em contato com o Administrador.",
                    "Contatos",
                    false,
                    "",
                    () => navigate("/contacts", true),
                    true,
                    "warning",
                    true
                );
                break;
            default:
                setErrorMessage("Erro ao conectar no servidor");
        }
    };

    const handleLogin = async () => {
        if (loginBlocked) return;

        setErrorMessage("");

        if (!form.username || !form.password) {
            setErrorMessage("Preencha os campos");
            return;
        }

        setLoading(true);

        const extra = {};

        if (loginInfo?.useCaptcha) {
            if (!executeRecaptcha) {
                setErrorMessage("Não foi possível validar a segurança. Tente novamente em instantes.");
                setLoading(false);
                return;
            }

            const token = await executeRecaptcha("LOGIN").catch(() => null);

            if (!token) {
                setErrorMessage("Não foi possível validar a segurança. Tente novamente.");
                setLoading(false);
                return;
            }

            extra.token = token;
        }

        loginService.login(buildLoginData(form, extra))
            .then(onLoginSuccess)
            .catch(handleLoginError);
    };

    const handleEnterLogin = () => {
        if (loginBlocked) return;

        const now = Date.now();

        if (now - lastEnterLoginAt.current < ENTER_LOGIN_COOLDOWN_MS) return;

        lastEnterLoginAt.current = now;
        handleLogin();
    };

    const handleResetPassword = (loginValue) => {
        const targetLogin = (loginValue ?? form.username ?? "").trim();

        if (!targetLogin) {
            setErrorMessage("Informe seu usuário.");
            return;
        }

        const formData = new FormData();
        formData.append("login", targetLogin);
        setRecoveryOpen(true);
    };

    useGlobalKey("Enter", handleEnterLogin);

    return (
        <AuthLayout>
            <LoginCard
                form={form}
                onChange={handleInput}
                onSubmit={handleLogin}
                errorMessage={errorMessage}
                cityName={loginInfo?.cityName}
                loading={loading}
                loginBlocked={loginBlocked}
                onResetPassword={() => handleResetPassword()}
                onOpenSupport={() => setSupportOpen(true)}
            />

            <PasswordRecoveryModal
                open={recoveryOpen}
                setOpen={setRecoveryOpen}
                login={form.username}
                passwordComplexity={!!loginInfo?.passwordComplexity}
                onSuccess={() => navigate("/home")}
            />

            {
                twoFactor &&
                <TwoFactorModal
                    open={twoFactorOpen}
                    setOpen={setTwoFactorOpen}
                    twoFactor={twoFactor}
                    credentials={form}
                    useCaptcha={!!loginInfo?.useCaptcha}
                    onSuccess={onLoginSuccess}
                />
            }

            <TechnicalSupportModal open={supportOpen} setOpen={setSupportOpen}/>

            <Modal open={trustedDeviceOpen} setOpen={() => {}} isCloseClick={false} showCloseButton={false} className="w-[350px] max-w-full">
                <Modal.Body>
                    <div className="flex flex-col items-center gap-2 py-3">
                        <FaCircleCheck className="text-[72px] text-[#008C27]"/>
                        <h3 className="font-semibold text-center">Dispositivo confiável!</h3>
                    </div>
                </Modal.Body>
            </Modal>
        </AuthLayout>
    );
};

const Login = () => {
    const {data: loginInfo, isLoading} = useQuery({
        queryKey: ["login_info"],
        queryFn: () => loginService.getLoginInfo(),
        retry: false,
    });

    if (isLoading) {
        return (
            <AuthLayout>
                <span className="inline-block size-10 rounded-full border-4 border-solid border-white border-r-transparent animate-spin"/>
            </AuthLayout>
        );
    }

    if (loginInfo?.useCaptcha) {
        return (
            <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY} useEnterprise>
                <LoginContent loginInfo={loginInfo}/>
            </GoogleReCaptchaProvider>
        );
    }

    return <LoginContent loginInfo={loginInfo}/>;
};

export default Login;
